import { DownloadOutlined, FilePdfOutlined, LineChartOutlined } from '@ant-design/icons';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { cGradeOf } from '@shared/levels';
import { api } from '../api/client.js';
import { ExportButtons } from './ExportButtons.js';
import { downloadPositionPdf } from '../lib/positionPdf.js';
import { retireMark } from './OrgFlow.js';
import { PositionDetail } from './PositionDetail.js';
import { RetirementWaveModal } from './RetirementWaveModal.js';
import type { ExportTable } from '../lib/exportTable.js';
import type { Position } from '@shared/types';

/**
 * บัญชีอัตราของหน่วยงานหนึ่ง — เปิดจากตัวเลขบนการ์ดในผัง
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมยอดในหน้าต่างนี้ต้องตรงกับตัวเลขที่ผู้ใช้กด
 * ────────────────────────────────────────────────────────────────
 *
 * ผู้ใช้กดเลข 770 แล้วคาดว่าจะเห็นของ 770 รายการ ถ้าได้ 764 หรือ 812 ความน่าเชื่อถือ
 * ของทั้งผังหายไปทันที และไม่มีทางรู้ด้วยว่าเลขไหนผิด
 *
 * จึงกรองด้วย unitPath (เทียบเส้นทางแบบตรงตัว นับรวมหน่วยย่อยทุกชั้น) ไม่ใช่ unit
 * ซึ่งเทียบชื่อแบบหลวมและกวาดหน่วยที่ชื่อคล้ายกันเข้ามาด้วย
 *
 * ⚠ ห้ามเปลี่ยนไปใช้ unit เพื่อความสะดวก — ยอดจะเริ่มไม่ตรงกับการ์ดโดยไม่มีอะไรฟ้อง
 *   server/scripts/check-unitpath.ts เดินผังทั้งต้นแล้วเทียบยอดทุกโหนด ให้รันเมื่อแตะจุดนี้
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมต้องโหลดทีละหน้า และค้นหาที่ฝั่งเซิร์ฟเวอร์
 * ────────────────────────────────────────────────────────────────
 *
 * โหนดบนสุดมี 5,341 อัตรา ดึงมาทั้งก้อนแล้วค้นในเครื่องผู้ใช้ทำได้ก็จริง แต่เพดาน
 * ของ API อยู่ที่ 500 แถว ผลคือค้นแล้วไม่เจอทั้งที่ของมีอยู่ — ซึ่งอ่านได้ว่า
 * "ไม่มีอัตรานี้" แทนที่จะเป็น "หน้าต่างยังไม่ได้โหลดถึงแถวนั้น" คนละเรื่องกันคนละขั้ว
 *
 * จึงส่งคำค้นไปให้เซิร์ฟเวอร์กรองจากของทั้งหมด แล้วค่อยไล่โหลดทีละหน้า
 *
 * ────────────────────────────────────────────────────────────────
 *  ลำดับของรายการ
 * ────────────────────────────────────────────────────────────────
 *
 * เรียงจากตำแหน่งสูงลงล่างด้วย levelRank ที่ services/positions.ts ระดับเดียวกัน
 * เรียงตามชื่อตำแหน่ง — เป็นการเรียงฝั่งเซิร์ฟเวอร์ ไม่ใช่ฝั่งหน้าจอ
 *
 * ⚠ ห้ามมาเรียงซ้ำที่นี่ เพราะหน้าต่างนี้โหลดทีละหน้า การเรียงเฉพาะแถวที่โหลดมาแล้ว
 *   จะทำให้แถวของหน้าที่สองแทรกขึ้นไปอยู่เหนือแถวของหน้าแรก แล้วลำดับจะสลับ
 *   ทุกครั้งที่กด "แสดงเพิ่ม" โดยที่ไม่มีอะไรฟ้อง
 */
export type PositionListMode = 'all' | 'occupied' | 'vacant' | 'retiring';

const PAGE = 200;

const TITLE: Record<PositionListMode, string> = {
  all: 'กรอบอัตรากำลัง',
  occupied: 'จำนวนคน',
  retiring: 'เกษียณอีก 5 ปี',
  vacant: 'ตำแหน่งที่ว่าง',
};

const NOTE: Record<PositionListMode, string> = {
  all: 'ทุกอัตราในหน่วยงานนี้ ทั้งที่มีผู้ครองและที่ว่าง',
  occupied: 'เฉพาะอัตราที่มีผู้ครองอยู่',
  /* ⚠ เขียนให้ชัดว่านี่เป็นส่วนหนึ่งของ "จำนวนคน" ไม่ใช่ยอดแยกอีกก้อน
     ผู้ใช้ที่เปิดจากการ์ดเพิ่งเห็นสามตัวเลขเรียงกัน ถ้าไม่บอก จะอ่านว่าบวกกันได้ */
  retiring: 'ผู้ครองที่จะเกษียณภายใน 5 ปี — เป็นส่วนหนึ่งของจำนวนคน ไม่ใช่ยอดแยก',
  vacant: 'เฉพาะอัตราที่ยังไม่มีผู้ครอง',
};

const EMPTY: Record<PositionListMode, string> = {
  all: 'หน่วยงานนี้ไม่มีอัตราในระบบ',
  occupied: 'หน่วยงานนี้ไม่มีอัตราที่มีผู้ครองอยู่',
  retiring: 'หน่วยงานนี้ไม่มีผู้ครองที่จะเกษียณภายใน 5 ปี',
  vacant: 'หน่วยงานนี้ไม่มีตำแหน่งที่ว่าง',
};

const STATUS_OF: Record<PositionListMode, 'all' | 'occupied' | 'vacant' | 'retiring'> = {
  all: 'all',
  occupied: 'occupied',
  /* ⚠ ต้องส่ง 'retiring' ไปให้เซิร์ฟเวอร์กรอง ห้ามตกกลับไปเป็น 'occupied' แล้วมากรองที่หน้าจอ
     หน้าต่างนี้โหลดทีละ 200 แถว การกรองเฉพาะแถวที่โหลดมาแล้วจะได้ยอดไม่ตรงกับเลขบนการ์ด */
  retiring: 'retiring',
  vacant: 'vacant',
};

/**
 * ขนาดก้อนที่ใช้ไล่ดึงทุกแถวตอนส่งออก
 *
 * ⚠ 500 คือเพดานของ /api/positions (PAGING_QUERY.limit.maximum ใน routes/index.ts)
 *   ส่งมากกว่านี้ไม่ได้ช่วยให้เร็วขึ้น — คำขอจะถูกปฏิเสธด้วย 400 ทั้งก้อน
 *   แล้วปุ่มจะขึ้นว่าสร้างไฟล์ไม่สำเร็จโดยที่ไม่มีใครเดาถูกว่าเพราะอะไร
 */
const EXPORT_CHUNK = 500;

export function PositionListPanel({
  unitPath,
  unitName,
  mode,
  width,
  onResize,
  onClose,
}: {
  unitPath: string;
  unitName: string;
  mode: PositionListMode;
  /** ความกว้างที่ผู้ใช้ลากไว้ (px) — null คือยังไม่เคยลาก ให้ใช้ค่าแบ่งครึ่งจาก CSS */
  width: number | null;
  onResize: (px: number | null) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const [needle, setNeedle] = useState('');
  /* อัตราที่กำลังกางดูอยู่ — null = กำลังดูบัญชีรายชื่อ
     เก็บทั้งก้อน Position ไม่ใช่แค่ posId เพราะข้อมูลอัตราติดมากับรายการอยู่แล้ว
     ถ้าเก็บแค่คีย์แล้วไปโหลดใหม่ จะมีจังหวะจอว่างทั้งที่ของอยู่ในมือแล้ว */
  const [open, setOpen] = useState<Position | null>(null);
  /* กราฟคลื่นเกษียณเปิดซ้อนอยู่บนแผงนี้ ไม่ได้แทนที่เนื้อหาในแผงเหมือน `open`
     ⚠ ต้องเป็นหน้าต่างซ้อน ไม่ใช่สลับเนื้อหาในแผง — ผู้ใช้เปิดกราฟเพื่อดูว่าคนกระจุกปีไหน
       แล้วกลับไปหาแถวในบัญชีต่อ ถ้าสลับเนื้อหา ตำแหน่งที่เลื่อนค้างไว้ในบัญชีจะหายทุกครั้ง */
  const [wave, setWave] = useState(false);
  /* สำเนาของ `wave` ที่อ่านได้จากใน listener โดยไม่ต้องผูก effect ของ Esc ไว้กับมัน
     ⚠ ต้องเป็น ref ไม่ใช่ตัวแปร state ตรง ๆ — ถ้าใส่ `wave` ลงใน deps ของ effect นั้น
       effect จะถอดแล้วติดตั้ง listener ใหม่ทุกครั้งที่เปิด/ปิดกราฟ ซึ่งทำให้ returnTo
       ที่จำไว้ตอนเปิดแผงถูกเขียนทับด้วย element ที่โฟกัสอยู่ ณ ตอนนั้น แล้วโฟกัสจะไม่กลับ
       ไปที่ตัวเลขบนการ์ดที่ผู้ใช้กดเข้ามาอีกเลย */
  const waveOpen = useRef(false);
  waveOpen.current = wave;
  const closeBtn = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLElement>(null);

  /* ⚠ แปลงตรง ๆ เป็นตาราง ไม่ใช่ ternary — ตอนมีสองโหมด ternary ยังอ่านออก
     แต่พอเพิ่มโหมดที่สาม โหมดที่ลืมใส่จะตกไปเป็น 'all' เงียบ ๆ แล้วหน้าต่าง
     "ตำแหน่งที่ว่าง" จะขึ้นอัตราที่มีคนครองปนมาโดยไม่มีอะไรฟ้อง */
  const status = STATUS_OF[mode];

  /* ปิดด้วย Esc และคืนโฟกัสให้ปุ่มที่เปิดหน้าต่างนี้ — แบบเดียวกับโมดอลอื่นในระบบ */
  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    /* ⚠ Esc ตอนกางรายละเอียดอยู่ = ถอยกลับไปบัญชีรายชื่อ ไม่ใช่ปิดทั้งหน้าต่าง
       ปิดรวดเดียวจะพาผู้ใช้กลับไปถึงผัง ทั้งที่เขาแค่อยากดูอัตราถัดไปในหน่วยเดิม */
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      /* ⚠ มีหน้าต่างกราฟเปิดซ้อนอยู่ = Esc เป็นของหน้าต่างนั้น ไม่ใช่ของแผงนี้
         ต้องเช็กที่นี่ ไม่ใช่หวังพึ่ง stopPropagation ในหน้าต่างซ้อน — ทั้งสองตัวดัก
         keydown ที่ document เหมือนกัน stopPropagation หยุดได้แค่การไต่ขึ้นไปยัง
         element ชั้นบน หยุด listener ตัวอื่น "ที่ element เดียวกัน" ไม่ได้
         (ตัวที่หยุดได้คือ stopImmediatePropagation แต่ก็ต้องถูกลงทะเบียนก่อน
         ซึ่งกลับกัน เพราะแผงนี้ลงทะเบียนตั้งแต่ตอนเปิดแผง = ก่อนหน้าต่างซ้อนเสมอ)
         ผลถ้าไม่เช็ก: กด Esc ครั้งเดียวปิดทั้งกราฟและแผง แล้วเด้งกลับไปถึงผัง */
      if (waveOpen.current) return;
      setOpen((cur) => {
        if (cur) return null;
        onClose();
        return null;
      });
    };
    document.addEventListener('keydown', onKey);
    /* ⚠ ไม่ล็อก body.overflow เหมือนตอนเป็นหน้าต่างซ้อน — แผงนี้แบ่งจอกับผัง
       ผังฝั่งซ้ายยังต้องเลื่อนและกดได้ตามปกติขณะแผงเปิดอยู่ */
    closeBtn.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      returnTo?.focus?.();
    };
  }, [onClose]);

  /* เปลี่ยนหน่วยหรือเปลี่ยนโหมด = เริ่มค้นใหม่ ไม่งั้นคำค้นของหน่วยก่อนหน้าจะค้างอยู่ */
  useEffect(() => {
    setQ('');
    setNeedle('');
    setOpen(null);
  }, [unitPath, mode]);

  /* หน่วงคำค้น — ไม่งั้นพิมพ์ชื่อยาว ๆ หนึ่งชื่อจะยิงคำขอไปสิบกว่าครั้ง */
  useEffect(() => {
    const t = setTimeout(() => setNeedle(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  /* ยอดของหน่วยนี้ทั้งหมด — ต้องไม่ขยับตามคำค้น เพราะนี่คือเลขที่ผู้ใช้กดเข้ามา */
  const baseline = useQuery({
    queryKey: ['unit-positions-total', unitPath, mode],
    queryFn: () => api.positions({ unitPath, status, limit: 1 }),
    select: (d) => d.total,
  });

  const list = useInfiniteQuery({
    queryKey: ['unit-positions', unitPath, mode, needle],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api.positions({
        unitPath,
        status,
        q: needle || undefined,
        limit: PAGE,
        offset: pageParam as number,
      }),
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.items.length, 0);
      return loaded < last.total ? loaded : undefined;
    },
  });

  const pages = list.data?.pages ?? [];
  const rows = pages.flatMap((p) => p.items);
  const found = pages[0]?.total ?? 0;
  const searching = needle.length > 0;

  /* ── ลากขอบ "ซ้าย" เพื่อปรับความกว้าง ───────────────────────────
     ⚠ แผงนี้อยู่คอลัมน์ขวาแล้ว เส้นจับลากจึงอยู่ขอบซ้ายของแผง และความกว้าง
       คำนวณจาก box.right ไม่ใช่ box.left — ลากไปทางซ้ายคือแผงกว้างขึ้น
       ถ้าเผลอใช้สูตรเดิม (ev.clientX - box.left) แผงจะแคบลงตอนลากไปทางซ้าย
       ซึ่งตรงข้ามกับสิ่งที่มือกำลังทำ และไม่มี error อะไรให้เห็น

     ⚠ วัดจากกรอบของ .ocv-body ไม่ใช่จากขอบจอ เพราะแถบด้านบนกับขอบของหน้าจอ
       ทำให้สองค่านี้ต่างกัน ถ้าใช้ clientX ดิบ แผงจะกระโดดทันทีที่เริ่มลาก
     ⚠ กันความกว้างต่ำสุดไว้ทั้งสองฝั่ง — แผงที่แคบกว่า MIN อ่านสามคอลัมน์ไม่ได้
       และผังที่เหลือน้อยกว่า MIN_STAGE ก็กลายเป็นแถบเปล่าที่ยังกินที่ */
  /* ── ส่งออกบัญชีรายการ ───────────────────────────────────────
     ⚠ ไล่ดึงทุกหน้าใหม่ตอนกด ไม่ได้ใช้ rows ที่โหลดมาแล้ว

     rows มีเฉพาะหน้าที่ผู้ใช้กด "แสดงเพิ่ม" ไปถึง ถ้าเอาไปทำไฟล์ คนที่เพิ่งเปิดแผง
     จะได้ไฟล์ 200 แถวจากยอด 770 โดยที่ในไฟล์ไม่มีอะไรบอกว่าขาด แล้วมันจะถูก
     แนบเรื่องไปทั้งอย่างนั้น — ผิดแบบที่ไม่มีใครจับได้จนกว่าจะมีคนไปนับเอง

     ⚠ ส่งคำค้นไปด้วย (needle) เพราะสิ่งที่ผู้ใช้เห็นอยู่คือผลที่กรองแล้ว
       ไฟล์ที่ได้ต้องตรงกับหน้าจอ ไม่ใช่ทั้งหน่วยงาน และบอกไว้ในหัวเรื่องด้วย */
  const buildExport = useCallback(async (): Promise<ExportTable> => {
    const items: Position[] = [];
    for (let offset = 0; ; offset += EXPORT_CHUNK) {
      const page = await api.positions({
        unitPath,
        status,
        q: needle || undefined,
        limit: EXPORT_CHUNK,
        offset,
      });
      items.push(...page.items);
      if (items.length >= page.total || page.items.length === 0) break;
    }

    return {
      title: `${TITLE[mode]} ${unitName}`,
      subtitle: `${NOTE[mode]}${needle ? ` · เฉพาะที่ตรงกับคำค้น “${needle}”` : ''}`,
      /* ⚠ โหมด "เกษียณอีก 5 ปี" ต้องมีคำเตือนนี้เสมอ — ไฟล์ที่หลุดจากหน้าจอไปแล้ว
         ไม่มีการ์ดข้าง ๆ คอยบอกว่ายอดนี้เป็นเซตย่อยของจำนวนคน ไม่ใช่ยอดแยก */
      warnings:
        mode === 'retiring'
          ? ['รายชื่อนี้เป็นส่วนหนึ่งของผู้ครองอัตราในหน่วยงาน ไม่ใช่ยอดที่นำไปบวกกับจำนวนคน']
          : [],
      columns: [
        { key: 'posNo', label: 'เลขที่ตำแหน่ง', width: 16 },
        { key: 'title', label: 'ชื่อตำแหน่ง', width: 30 },
        { key: 'level', label: 'ระดับ', width: 18 },
        { key: 'cgrade', label: 'เทียบซี', width: 10 },
        { key: 'line', label: 'สายงาน', width: 22 },
        { key: 'category', label: 'ประเภทบุคลากร', width: 16 },
        { key: 'unit', label: 'หน่วยงาน', width: 30 },
        { key: 'status', label: 'สถานะ', width: 12 },
        { key: 'who', label: 'ผู้ครอง / ผู้รักษาการ', width: 26 },
        { key: 'retire', label: 'เกษียณ (อัตราว่าง 1 ต.ค.)', width: 16, numeric: true },
        { key: 'note', label: 'หมายเหตุ', width: 26 },
      ],
      rows: items.map((it) => ({
        posNo: it.posNo,
        title: it.title,
        level: it.level || null,
        cgrade: cGradeOf(it.level),
        line: it.line || null,
        category: it.category,
        unit: it.orgPath[it.orgPath.length - 1] ?? it.unit,
        status: it.vacant ? 'ว่าง' : 'มีผู้ครอง',
        /* ⚠ อัตราว่างที่มีผู้รักษาการต้องขึ้นชื่อผู้รักษาการพร้อมคำกำกับ
           ไม่ใช่ขึ้นชื่อเปล่า ๆ ซึ่งในไฟล์จะอ่านไม่ออกเลยว่าคนนี้ไม่ได้ครองอัตรา */
        who: it.incumbent?.name ?? (it.acting ? `รักษาการ · ${it.acting.name}` : null),
        retire: it.incumbent?.retireFiscalYear ?? null,
        note: it.vacant
          ? it.vacantMonths === null
            ? 'ไม่ทราบว่าว่างตั้งแต่เมื่อไร'
            : `ว่างมาแล้ว ${Math.floor(it.vacantMonths / 12) > 0 ? `${Math.floor(it.vacantMonths / 12)} ปี ` : ''}${it.vacantMonths % 12} เดือน`
          : null,
      })),
    };
  }, [mode, needle, status, unitName, unitPath]);

  const MIN = 360;
  const MIN_STAGE = 320;
  function onGrip(e: ReactPointerEvent<HTMLDivElement>) {
    const box = panel.current?.parentElement?.getBoundingClientRect();
    if (!box) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    /* ⚠ Math.max ครอบนอกสุด — ถ้าจอแคบจน box.width - MIN_STAGE น้อยกว่า MIN
       การ min ทีหลังจะได้ค่าต่ำกว่าความกว้างต่ำสุดที่ตั้งไว้ */
    const move = (ev: PointerEvent) => {
      onResize(Math.max(MIN, Math.min(box.right - ev.clientX, box.width - MIN_STAGE)));
    };
    /* ระหว่างลาก ห้ามให้เกิดการเลือกข้อความ ไม่งั้นลากผ่านรายการแล้วชื่อคนจะถูกไฮไลต์ไปทั้งแผง */
    const prevSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    const up = () => {
      document.body.style.userSelect = prevSelect;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }

  return (
    <aside
      className="plp"
      ref={panel}
      style={width === null ? undefined : { width: `${width}px` }}
      aria-label={`${TITLE[mode]} ของ ${unitName}`}
    >
      {/* จับลากได้ทั้งเส้น ไม่ใช่แค่จุดเล็ก ๆ · กดสองครั้งกลับไปแบ่งครึ่ง
          ⚠ ต้องอยู่ "ก่อน" .plp-inner ใน DOM เพราะแผงอยู่คอลัมน์ขวา เส้นจับจึงอยู่ขอบซ้าย
            (.plp เป็น flex แนวนอน ลำดับใน DOM คือลำดับซ้าย-ขวา)
          ⚠ ใช้ <div> ไม่ใช่ปุ่ม เพราะไม่ใช่การกระทำเดียวจบ แต่เป็นการลากต่อเนื่อง
            ผู้ใช้คีย์บอร์ดปรับความกว้างไม่ได้ ซึ่งรับได้ เพราะแผงยังอ่านครบทุกอย่างที่ความกว้างเริ่มต้น */}
      <div
        className="plp-grip"
        onPointerDown={onGrip}
        onDoubleClick={() => onResize(null)}
        title="ลากเพื่อปรับความกว้าง · กดสองครั้งเพื่อกลับไปแบ่งครึ่ง"
        aria-hidden="true"
      />

      <div className="plp-inner">
        <header className="pmodal-hd">
          {/* ปุ่มกลับอยู่ในหัวหน้าต่าง ไม่ใช่ท้ายเนื้อหา — บัญชีของกรมหนึ่งยาวหลายร้อยแถว
              ปุ่มที่ต้องเลื่อนลงไปหาถึงจะเจอ เท่ากับไม่มีปุ่ม */}
          {open && (
            <button type="button" className="pmodal-back-btn" onClick={() => setOpen(null)}>
              {/* ลูกศรวาดด้วย CSS ไม่ใช่ตัวอักษร — ดูเหตุผลที่ .pmodal-back-btn i ใน styles.css */}
              <i aria-hidden="true" /> กลับไปที่รายชื่อ
            </button>
          )}
          <div>
            {open ? (
              <>
                <span className="kind">
                  {open.vacant ? 'อัตราว่าง' : 'อัตราที่มีผู้ครอง'}
                  {open.posNo && ` · เลขที่ ${open.posNo}`}
                </span>
                <b>{open.title}</b>
                <span className="sub">
                  {open.level || 'ไม่ระบุระดับ'} — {open.orgPath[open.orgPath.length - 1] ?? open.unit}
                </span>
              </>
            ) : (
              <>
                <span className="kind">{TITLE[mode]}</span>
                <b>
                  {baseline.data === undefined
                    ? 'กำลังโหลด…'
                    : `${baseline.data.toLocaleString('th-TH')} อัตรา`}
                </b>
                <span className="sub">{unitName} — {NOTE[mode]}</span>
              </>
            )}
          </div>

          {/* ────────── ส่งออก ──────────
              สองสถานะของแผงนี้ส่งออกคนละอย่าง และต้องไม่สลับกัน

                กางรายละเอียดอัตราอยู่ → เอกสารของ "อัตรานั้น" (PDF อย่างเดียว)
                ดูบัญชีรายชื่ออยู่      → ตารางของ "รายการที่เห็นอยู่" (PDF + Excel)

              ⚠ อัตราเดี่ยวไม่มีปุ่ม Excel โดยตั้งใจ — ไฟล์ Excel ที่มีข้อมูลแถวเดียว
                ไม่มีอะไรให้กรองหรือคำนวณต่อ ปุ่มที่กดแล้วได้ของไร้ประโยชน์
                แย่กว่าไม่มีปุ่ม ส่วนคนที่อยากได้หลายอัตราเป็น Excel กดจากบัญชีรายชื่อได้อยู่แล้ว */}
          {/* ⚠ ต้องห่อใน <div> — .pmodal-hd > button ถูกเขียนไว้สำหรับปุ่มปิด ✕ ปุ่มเดียว
              และบังคับทุกปุ่มลูกโดยตรงให้เป็นสี่เหลี่ยม 36×36 ไม่มีขอบ พร้อม margin-left:auto
              ปุ่มส่งออกที่วางเปล่า ๆ ตรงนี้จะโดนกฎนั้นกินแล้วดันปุ่มปิดจนแถวเบี้ยว
              (กับดักตัวเดียวกับที่คอมเมนต์เหนือกฎนั้นใน styles.css เตือนไว้) */}
          <div className="plp-tools">
            {/* ────────── กราฟคลื่นเกษียณ ──────────
                อยู่ก่อนปุ่มดาวน์โหลด เพราะเป็นการ "ดูต่อ" ไม่ใช่การ "เอาออกไป"
                ลำดับในแถบเครื่องมือควรเป็นอ่านก่อนแล้วค่อยส่งออก

                ⚠ ซ่อนตอนกางรายละเอียดอัตราเดี่ยว — กราฟเป็นของทั้งหน่วยงาน
                  ปุ่มที่ค้างอยู่ตอนดูอัตราเดียวจะอ่านว่าเป็นกราฟของอัตรานั้น */}
            {!open && (
              <button
                type="button"
                className="icobtn"
                /* ⚠ aria-haspopup/aria-expanded ไม่ใช่ aria-pressed — ปุ่มนี้เปิดหน้าต่าง
                   ไม่ใช่สวิตช์เปิด/ปิดของอะไรในหน้า และ .icobtn[aria-pressed='true']
                   จะระบายปุ่มเป็นสีทึบ ซึ่งเป็นหน้าตาของ "โหมดที่ค้างอยู่" คนละความหมาย */
                aria-haspopup="dialog"
                aria-expanded={wave}
                onClick={() => setWave(true)}
                aria-label="ดูกราฟคลื่นเกษียณของหน่วยงานนี้"
                /* ⚠ data-tip ไม่ใช่ title — .icobtn วาดคำอธิบายเองด้วย ::after */
                data-tip="คลื่นเกษียณ 5 ปี"
              >
                <LineChartOutlined />
              </button>
            )}
            {open ? <PositionPdfButton p={open} /> : <ExportButtons build={buildExport} compact />}
          </div>

          <button type="button" onClick={onClose} aria-label="ปิดหน้าต่าง" ref={closeBtn}>✕</button>
        </header>

        {open ? (
          <div className="rosbody">
            <PositionDetail p={open} />
          </div>
        ) : (
        /* has-sticky-groups = ยกช่องว่างด้านบนออกจากกล่องเลื่อน เพื่อให้หัวคั่นระดับ
           ติดที่ขอบบนพอดีโดยไม่บังเส้นขอบของแถวที่อยู่เหนือมัน (ดู styles.css) */
        <div className="rosbody has-sticky-groups">
          <div className="rosbar">
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="ค้นหาชื่อตำแหน่ง ระดับ เลขที่ตำแหน่ง หรือชื่อผู้ครอง"
              aria-label="ค้นหาในบัญชีอัตรา"
            />
          </div>

          {list.isPending && <p className="state">กำลังโหลดบัญชีอัตรา…</p>}
          {list.isError && <p className="state error">{String(list.error)}</p>}

          {list.data && (
            <>
              {/* คำค้นกรองจากของทั้งหมดในหน่วย ไม่ใช่จากแถวที่โหลดมาแล้ว จึงบอกยอดที่พบได้จริง */}
              {searching && (
                <p className="plm-note">
                  พบ {found.toLocaleString('th-TH')} อัตราที่ตรงกับ “{needle}” จากทั้งหมด{' '}
                  {(baseline.data ?? found).toLocaleString('th-TH')}
                </p>
              )}

              {rows.length === 0 ? (
                /* ⚠ "ไม่พบตามคำค้น" กับ "หน่วยนี้ไม่มีของประเภทนี้" คนละเรื่องกัน
                   หน่วยที่ไม่มีอัตราว่างเลยแล้วขึ้นว่าไม่พบตามคำค้น ทั้งที่ยังไม่ได้ค้นอะไร
                   อ่านแล้วเหมือนระบบค้นพลาด ผู้ใช้จะพิมพ์ค้นซ้ำไปเรื่อยโดยไม่มีวันเจอ */
                <p className="state">{searching ? 'ไม่พบอัตราที่ตรงกับคำค้น' : EMPTY[mode]}</p>
              ) : (
                <>
                  <ul className="plm-list">
                    {rows.map((p, i) => (
                      <Group key={p.posId} p={p} prev={rows[i - 1]}>
                        <Row p={p} onOpen={() => setOpen(p)} />
                      </Group>
                    ))}
                  </ul>

                  {list.hasNextPage && (
                    <button
                      type="button"
                      className="plm-more"
                      disabled={list.isFetchingNextPage}
                      onClick={() => void list.fetchNextPage()}
                    >
                      {list.isFetchingNextPage
                        ? 'กำลังโหลด…'
                        : `แสดงเพิ่มอีก ${Math.min(PAGE, found - rows.length).toLocaleString('th-TH')} อัตรา`}
                      <span> (แสดงอยู่ {rows.length.toLocaleString('th-TH')} จาก {found.toLocaleString('th-TH')})</span>
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>
        )}
      </div>

      {/* หน้าต่างกราฟเปิดซ้อนทั้งจอ จึงต้องอยู่นอก .plp-inner ที่ตัดส่วนเกินทิ้ง
          ⚠ วางไว้ในสายเดียวกันกับแผง ไม่ได้ย้ายไป portal — หน้าต่างนี้ใช้ position: fixed
            ซึ่งหลุดจากกล่องแม่อยู่แล้ว และการอยู่ในสายเดียวกันทำให้โฟกัสวิ่งกลับมาที่ปุ่มเดิม
            ตอนปิดได้เอง โดยไม่ต้องจัดการเอง */}
      {wave && (
        <RetirementWaveModal
          unitPath={unitPath}
          unitName={unitName}
          onClose={() => setWave(false)}
        />
      )}
    </aside>
  );
}

/**
 * ปุ่มส่งออกเอกสารของอัตราเดียว
 *
 * ⚠ ไม่ได้ใช้ ExportButtons เพราะตัวนั้นบังคับให้มีปุ่ม Excel คู่มาด้วยเสมอ
 *   (pdfOverride เปลี่ยนได้แต่ฝั่ง PDF) ซึ่งไม่เหมาะกับของชิ้นเดียว — ดูเหตุผลที่จุดเรียกใช้
 *
 * ⚠ ต้องมีสถานะ "กำลังสร้าง" ให้เห็น ปุ่มนี้ยิงคำขอสองรายการแล้วโหลด pdfmake อีก ~400 KB
 *   ครั้งแรกที่กดจึงหน่วงหลายวินาทีบนเน็ตช้า ถ้าปุ่มไม่เปลี่ยนอะไรเลย ผู้ใช้จะกดซ้ำ
 *   แล้วได้ไฟล์ซ้ำหลายใบในโฟลเดอร์ดาวน์โหลด (บทเรียนเดียวกับ ExportButtons)
 */
export function PositionPdfButton({ p }: { p: Position }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  return (
    <button
      type="button"
      className="icobtn"
      disabled={busy}
      onClick={() => {
        if (busy) return;
        setBusy(true);
        setFailed(null);
        downloadPositionPdf(p)
          /* ⚠ ต้องบอกผู้ใช้เมื่อพัง ไม่ใช่เงียบ — ปุ่มที่กดแล้วไม่มีอะไรเกิดขึ้น
             ผู้ใช้จะกดซ้ำอีกหลายครั้งแล้วสรุปว่าระบบพัง */
          .catch((err: unknown) =>
            setFailed(err instanceof Error ? err.message : 'สร้างไฟล์ไม่สำเร็จ'),
          )
          .finally(() => setBusy(false));
      }}
      aria-label="ดาวน์โหลดรายละเอียดอัตรานี้เป็นไฟล์ PDF"
      /* ⚠ ใช้ data-tip ไม่ใช่ title — .icobtn วาดคำอธิบายเองด้วย ::after
         ใส่ title ด้วยจะได้คำอธิบายซ้อนกันสองอัน */
      data-tip={failed ?? (busy ? 'กำลังสร้างไฟล์…' : 'ดาวน์โหลด PDF ของอัตรานี้')}
    >
      {busy ? <DownloadOutlined spin /> : <FilePdfOutlined />}
    </button>
  );
}

/**
 * หัวคั่นเมื่อระดับเปลี่ยน — ทำให้ลำดับ "สูงลงต่ำ" มองเห็นได้ ไม่ใช่แค่เป็นจริง
 *
 * ⚠ ไม่ใส่จำนวนอัตราไว้บนหัวคั่น เพราะรายการโหลดทีละหน้า ระดับที่คร่อมรอยต่อหน้า
 *   จะนับได้แค่ส่วนที่โหลดมาแล้ว แล้วเลขบนหัวคั่นจะน้อยกว่าความจริงโดยไม่มีอะไรบอก
 *   หัวคั่นที่ไม่มีเลขบอกน้อยกว่า แต่ไม่มีทางบอกผิด
 */
function Group({ p, prev, children }: { p: Position; prev?: Position; children: React.ReactNode }) {
  const changed = !prev || prev.level !== p.level;
  if (!changed) return <>{children}</>;

  const c = cGradeOf(p.level);
  return (
    <>
      <li className="plm-group" aria-hidden="true">
        <b>{p.level || 'ไม่ระบุระดับ'}</b>
        {/* ผู้บริหารยังพูดเป็นซี — ระดับที่ไม่มีการเทียบซีที่อ้างอิงได้จะไม่แสดงอะไร ไม่ใช่เดาให้ */}
        {c && <span>{c}</span>}
      </li>
      {children}
    </>
  );
}

function Row({ p, onOpen }: { p: Position; onOpen: () => void }) {
  /* หน่วยงานที่สังกัดโดยตรง — ตัดชั้นบนออก เพราะทุกแถวในหน้าต่างนี้อยู่ใต้หน่วยเดียวกันอยู่แล้ว
     เขียนซ้ำทุกแถวจะกินความกว้างไปกับข้อความที่เหมือนกันหมด
     ส่วนระดับก็ไม่เขียนซ้ำ เพราะหัวคั่นด้านบนบอกไปแล้ว */
  const unit = p.orgPath[p.orgPath.length - 1] ?? '—';

  /* สามช่อง: ตำแหน่ง · หน่วยงาน · ผู้ครอง
     หน้าต่างนี้กว้างเกือบเต็มจอ ถ้าเหลือสองช่องคือชื่อตำแหน่งชิดซ้ายกับชื่อคนชิดขวา
     ตรงกลางจะเป็นที่ว่างพันกว่าพิกเซล แล้วสายตาต้องกวาดข้ามไปทั้งแถวเพื่ออ่านคู่เดียว */
  /* ทั้งแถวเป็นปุ่ม ไม่ใช่ลิงก์เล็ก ๆ ท้ายบรรทัด — เป้ากดใหญ่กว่ามากและใช้บนจอสัมผัสได้
     ใช้ <button> จริงเพื่อให้ไล่ด้วย Tab และกด Enter ได้ แบบเดียวกับแผงรายชื่อในผัง */
  return (
    <li className={`plm-row${p.vacant ? ' is-vacant' : ''}`}>
      <button type="button" className="plm-hit" onClick={onOpen}>
      <span className="plm-main">
        <b>{p.title}</b>
        <span className="plm-meta">{p.posNo ? `เลขที่ ${p.posNo}` : 'ไม่มีเลขที่ตำแหน่ง'}</span>
      </span>

      <span className="plm-unit">{unit}</span>

      <span className="plm-who">
        {p.incumbent ? (
          <>
            <b>{p.incumbent.name}</b>
            <Retire inc={p.incumbent} />
          </>
        ) : (
          <>
            {/* ⚠ "ว่าง" ต้องมาก่อนชื่อผู้รักษาการเสมอ ไม่ใช่ขึ้นชื่อคนแล้วค่อยบอกว่าว่าง
                ผู้รักษาการไม่ใช่ผู้ครอง อัตรานี้ยังนับเป็นอัตราว่างในทุกยอดบนจอ
                ถ้าสลับลำดับ ผู้อ่านจะนับอัตรานี้ว่ามีคนแล้ว แล้วยอดบนการ์ดจะดูขัดกันเอง */}
            <b className="is-vacant">ว่าง</b>
            {/* ⚠ "ไม่ทราบ" ไม่ใช่ช่องว่าง — ต้องแยกให้ออกจาก "ว่างเพิ่งเกิด" */}
            <span>
              {p.vacantMonths === null
                ? 'ไม่ทราบว่าว่างตั้งแต่เมื่อไร'
                : `ว่างมาแล้ว ${Math.floor(p.vacantMonths / 12) > 0 ? `${Math.floor(p.vacantMonths / 12)} ปี ` : ''}${p.vacantMonths % 12} เดือน`}
            </span>
            {p.acting && <span className="plm-acting">รักษาการ · {p.acting.name}</span>}
          </>
        )}
      </span>
      </button>
    </li>
  );
}

/**
 * สถานะเกษียณของผู้ครอง
 *
 * ป้ายสีใช้เกณฑ์เดียวกับผังและแผงรายชื่อ (retireMark — ≤2 ปีทอง · ≤5 ปีจาง)
 * ห้ามตั้งเลขเกณฑ์ซ้ำที่นี่ เหตุผลอยู่ในหัวข้อ "เกณฑ์ใกล้เกษียณ" ของ OrgFlow.tsx
 *
 * คนที่ยังไกลเกษียณก็ต้องบอกปีไว้ ไม่ใช่เว้นว่าง — บัญชีอัตราที่บางแถวมีข้อมูลเกษียณ
 * บางแถวไม่มี อ่านได้ว่า "แถวที่ว่างคือคนที่ไม่เกษียณ" ซึ่งไม่จริง
 */
function Retire({ inc }: { inc: NonNullable<Position['incumbent']> }) {
  const mark = retireMark(inc);
  if (mark) return <b className={`rt is-${mark.tone}`}>{mark.label}</b>;

  /* ⚠ ไม่ทราบวันเกษียณ = ไฟล์ต้นทางไม่มีวันเกิด ไม่ใช่ "ยังอีกนาน" — ต้องเขียนต่างกัน */
  return (
    <span>
      {inc.retireFiscalYear === null
        ? 'ไม่ทราบวันเกษียณ'
        : `เกษียณ 1 ต.ค. ${inc.retireFiscalYear}`}
    </span>
  );
}

/**
 * รายละเอียดอัตราเดียว แบบแผงเปิดด้านขวา — ใช้กับผังผู้บริหารตามตำแหน่ง
 *
 * ผู้ใช้สั่ง 19 ก.ย. 2569: "รายละเอียดของแต่ละคน อยากให้แสดงแบบเปิดขวา" เหมือนแผงบัญชีอัตรา
 * ⚠ ใช้หัวแผง/เนื้อหาชุดเดียวกับตอนกางอัตราใน PositionListPanel (คลาส .plp · .pmodal-hd · PositionDetail)
 *   ไม่ได้ทำหน้าตาชุดที่สอง — สองที่ต้องหน้าตาเหมือนกันเสมอ
 * ⚠ ไม่มีปุ่ม "กลับไปที่รายชื่อ" เพราะเปิดจากการ์ดบนผัง ไม่ได้เปิดจากบัญชี — ปิดแล้วกลับไปที่ผังเอง
 */
export function PositionSidePanel({
  p,
  width,
  onClose,
  sideToggle,
  grip,
}: {
  p: Position;
  /** ความกว้างเดียวกับแผงบัญชีอัตรา — null = แบ่งครึ่งตาม CSS */
  width: number | null;
  onClose: () => void;
  /** ปุ่มย้ายแผงซ้าย/ขวา — ตัวครอบถือสถานะเอง */
  sideToggle?: ReactNode;
  /** เส้นจับลากปรับความกว้าง (PanelGrip) */
  grip?: ReactNode;
}) {
  /* Esc ปิดแผง — เหมือนหน้าต่างอื่นในระบบ */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <aside
      className="plp"
      style={width === null ? undefined : { width: `${width}px` }}
      aria-label={`รายละเอียดอัตรา ${p.title}`}
    >
      {grip}
      <div className="plp-inner">
        <header className="pmodal-hd">
          <div>
            <span className="kind">
              {p.vacant ? 'อัตราว่าง' : 'อัตราที่มีผู้ครอง'}
              {p.posNo && ` · เลขที่ ${p.posNo}`}
            </span>
            <b>{p.title}</b>
            <span className="sub">
              {p.level || 'ไม่ระบุระดับ'} — {p.orgPath[p.orgPath.length - 1] ?? p.unit}
            </span>
          </div>
          <div className="plp-tools">
            {sideToggle}
            <PositionPdfButton p={p} />
          </div>
          <button type="button" onClick={onClose} aria-label="ปิดแผง">✕</button>
        </header>
        <div className="rosbody">
          <PositionDetail p={p} />
        </div>
      </div>
    </aside>
  );
}

/**
 * เส้นจับลากปรับความกว้างของแผงข้าง — ใช้กับแผงของผังผู้บริหาร (รายละเอียดคน · คลื่นเกษียณ)
 * ผู้ใช้สั่ง 19 ก.ย. 2569: "panel ซ้าย ขวา ลากเพื่อขยายด้านใดด้านหนึ่งได้"
 *
 * ⚠ ทิศการลากขึ้นกับฝั่งของแผง
 *   แผงขวา: เส้นอยู่ขอบซ้าย ความกว้าง = ขอบขวาของกรอบ − ตำแหน่งเมาส์
 *   แผงซ้าย: เส้นอยู่ขอบขวา ความกว้าง = ตำแหน่งเมาส์ − ขอบซ้ายของกรอบ
 *   (ย้ายเส้นไปขอบขวาด้วย CSS .is-panel-left .plp-grip — ไม่ย้าย DOM)
 * ⚠ กติกาความกว้างต่ำสุดชุดเดียวกับแผงบัญชีอัตรา — แผงไม่แคบกว่า 360 ผังเหลืออย่างน้อย 320
 */
export function PanelGrip({
  side,
  onResize,
}: {
  side: 'left' | 'right';
  onResize: (px: number | null) => void;
}) {
  const MIN = 360;
  const MIN_STAGE = 320;
  function onDown(e: ReactPointerEvent<HTMLDivElement>) {
    const box = e.currentTarget.parentElement?.parentElement?.getBoundingClientRect();
    if (!box) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const move = (ev: PointerEvent) => {
      const raw = side === 'right' ? box.right - ev.clientX : ev.clientX - box.left;
      onResize(Math.max(MIN, Math.min(raw, box.width - MIN_STAGE)));
    };
    const prevSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    const up = () => {
      document.body.style.userSelect = prevSelect;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }
  return (
    <div
      className="plp-grip"
      onPointerDown={onDown}
      onDoubleClick={() => onResize(null)}
      title="ลากเพื่อปรับความกว้าง · กดสองครั้งเพื่อกลับไปแบ่งครึ่ง"
      aria-hidden="true"
    />
  );
}
